import type { LoginOutput } from '@allocab/allocab-commons-shared-types';
import { storage } from '@frontend/shared';

import { constants } from '../constants';

/**
 * Télécharge un fichier authentifié depuis une route relative.
 *
 * Workflow :
 * 1. Construit l'URL absolue à partir de VITE_EXTRANET_API + path
 * 2. Fetch avec Bearer token (lu depuis le localStorage)
 * 3. Si 302 (signed URL S3), suit la redirection automatiquement (fetch follow par défaut)
 * 4. Récupère le blob
 * 5. Trigger un téléchargement navigateur via createObjectURL + <a download>
 *
 * Erreurs : 401/403 → throw. Le caller doit afficher un toast d'erreur.
 */
export const downloadFile = async (path: string, filename: string): Promise<void> => {
  const tokens = storage.getJSONItem<LoginOutput>('tokens');
  const accessToken = tokens?.accessToken;

  const baseUrl = constants.extranetApi || '';
  const url = path.startsWith('http') ? path : `${baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
  });

  if (!response.ok) {
    const error = new Error(`Téléchargement échoué (HTTP ${response.status}). ${response.statusText}`);
    (error as { status?: number }).status = response.status;
    throw error;
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Libère la mémoire après un délai (le download a démarré)
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};
